/**
 * Shared timezone-aware time formatter for ROP-teams.
 *
 * Ports the legacy `toCentral()` pattern from
 *   spfx-account-opening-v2/.../components/admin/KillListPanel.tsx:76-83
 * which renders ISO timestamps as Central-Time wall-clock with a " CT" suffix.
 *
 * Why this exists
 * ---------------
 * Gap G-CC-TZ-01 (docs/audit/port-gaps/01-cross-cutting-gaps.md): 29+ bare
 * `.toLocaleString()` call sites in ROP-teams modules had no `timeZone` option,
 * so the Teams webview/Node host would render timestamps in whatever zone the
 * underlying process was in (frequently UTC in Azure/Teams contexts). Users in
 * Central time saw 3pm cart events displayed as "8:00 PM" — compliance and FSR
 * confusion, plus SLA-breach math that didn't match the server.
 *
 * Rules
 * -----
 * - `null` / `undefined` / empty / invalid input → `''` (never `'Invalid Date'`).
 * - Default timezone = `America/Chicago` (sourced from ConfigContext in a future
 *   wave; hard-default here so these helpers work at bootstrap before config
 *   has loaded).
 * - When the effective timezone resolves to Central (`America/Chicago` or a
 *   recognized alias), append ` CT` suffix unless caller supplies an override.
 * - Pure `Intl.DateTimeFormat` — no `moment`, no `date-fns`, no external deps.
 * - No default export — consumers import by name so tree-shaking stays honest.
 */

const DEFAULT_TZ = 'America/Chicago';
const DEFAULT_CT_SUFFIX = ' CT';

/**
 * Timezone identifiers that represent US Central Time. When the caller passes
 * any of these (or omits `timeZone` entirely, which falls through to
 * `DEFAULT_TZ`), we append the ` CT` suffix by default.
 */
const CENTRAL_TZ_ALIASES: ReadonlySet<string> = new Set([
  'America/Chicago',
  'US/Central',
  'CST6CDT',
]);

export type TimeStyle = 'date' | 'time' | 'datetime';

export interface FormatLocalOptions {
  /** IANA timezone. Defaults to America/Chicago. */
  timeZone?: string;
  /** Which fields to render. Defaults to 'datetime'. */
  style?: TimeStyle;
  /**
   * Suffix appended after the formatted value. Defaults to ' CT' when the
   * resolved timezone is Central, '' otherwise. Pass `''` to suppress.
   */
  suffix?: string;
  /** 12-hour clock with AM/PM. Defaults to true. */
  hour12?: boolean;
}

/** Internal: coerce the incoming value to a valid Date, or null. */
function coerceDate(input: string | Date | null | undefined): Date | null {
  if (input === null || input === undefined) return null;
  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : input;
  }
  const trimmed = input.trim();
  if (trimmed === '') return null;
  const d = new Date(trimmed);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Internal: is this a Central-Time alias? */
function isCentralTz(tz: string): boolean {
  return CENTRAL_TZ_ALIASES.has(tz);
}

/** Internal: pick the default suffix for a timezone. */
function defaultSuffixFor(tz: string): string {
  return isCentralTz(tz) ? DEFAULT_CT_SUFFIX : '';
}

/** Internal: build an `Intl.DateTimeFormatOptions` object for a given style. */
function optsForStyle(
  style: TimeStyle,
  timeZone: string,
  hour12: boolean,
): Intl.DateTimeFormatOptions {
  const base: Intl.DateTimeFormatOptions = { timeZone };
  if (style === 'date') {
    return {
      ...base,
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    };
  }
  if (style === 'time') {
    return {
      ...base,
      hour: 'numeric',
      minute: '2-digit',
      hour12,
    };
  }
  // datetime
  return {
    ...base,
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12,
  };
}

/**
 * Primary formatter. Renders an ISO string / Date as a localized display
 * string in the target timezone, with an optional trailing suffix.
 *
 * @example
 *   formatLocal('2026-04-21T20:15:44.117Z')
 *     // '4/21/2026, 3:15 PM CT'
 *   formatLocal(null)
 *     // ''
 *   formatLocal('2026-04-21T20:15Z', { style: 'time' })
 *     // '3:15 PM CT'
 *   formatLocal('2026-04-21T20:15Z', { timeZone: 'America/New_York', suffix: ' ET' })
 *     // '4/21/2026, 4:15 PM ET'
 */
export function formatLocal(
  iso: string | Date | null | undefined,
  opts: FormatLocalOptions = {},
): string {
  const d = coerceDate(iso);
  if (!d) return '';

  const timeZone = opts.timeZone ?? DEFAULT_TZ;
  const style: TimeStyle = opts.style ?? 'datetime';
  const hour12 = opts.hour12 ?? true;
  const suffix = opts.suffix ?? defaultSuffixFor(timeZone);

  try {
    const fmt = new Intl.DateTimeFormat('en-US', optsForStyle(style, timeZone, hour12));
    return fmt.format(d) + suffix;
  } catch {
    // `Intl.DateTimeFormat` throws RangeError on unknown timezones — fall back
    // to the ISO string so the UI shows _something_ rather than a blank cell.
    return typeof iso === 'string' ? iso : d.toISOString();
  }
}

/** Date-only convenience wrapper (M/d/yyyy + optional CT suffix). */
export function formatLocalDate(iso: string | Date | null | undefined): string {
  return formatLocal(iso, { style: 'date' });
}

/** Time-only convenience wrapper (h:mm a + optional CT suffix). */
export function formatLocalTime(iso: string | Date | null | undefined): string {
  return formatLocal(iso, { style: 'time' });
}

/** Date + time convenience wrapper (same as `formatLocal` with no opts). */
export function formatLocalDateTime(iso: string | Date | null | undefined): string {
  return formatLocal(iso, { style: 'datetime' });
}

/**
 * Relative formatter — "just now", "5 min ago", "2h ago", "3d ago". For ages
 * older than 30 days, falls through to absolute `formatLocalDate`.
 *
 * Symmetric on future dates: returns "in 5 min", "in 2h", etc.
 *
 * Null/undefined/invalid → `''`.
 */
export function formatRelative(iso: string | Date | null | undefined): string {
  const d = coerceDate(iso);
  if (!d) return '';

  const diffMs = Date.now() - d.getTime();
  const past = diffMs >= 0;
  const absMs = Math.abs(diffMs);
  const absMin = Math.round(absMs / 60_000);

  if (absMin < 1) return 'just now';

  const render = (n: number, unit: string): string =>
    past ? `${n} ${unit} ago` : `in ${n} ${unit}`;

  if (absMin < 60) return render(absMin, absMin === 1 ? 'min' : 'min');

  const absH = Math.round(absMin / 60);
  if (absH < 24) return past ? `${absH}h ago` : `in ${absH}h`;

  const absD = Math.round(absH / 24);
  if (absD < 30) return past ? `${absD}d ago` : `in ${absD}d`;

  return formatLocalDate(iso);
}
